/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { getCmdsForArea } from '../utils/PRIVATE_chartUtils/utils';
import { Point } from '../utils/PRIVATE_visSVGUtils';
import { Scale } from '../UNSAFE_Chart';

type AreaSeriesProps = {
  yScale: Scale;
  color: ColorProps['color'];
  lineType?: 'straight' | 'curved';
  isHorizontal: boolean;
  isLog: boolean;
  areaColorOpacity: number;
  lineSegmentCoords: Point[][];
  bottomCoords?: Point[];
  isHighlighted: boolean;
  isBottomSegmentCurved: boolean;
  seriesIndex: number;
};

export function AreaSeries({
  yScale,
  lineType = 'straight',
  color,
  isHorizontal,
  isLog,
  areaColorOpacity,
  lineSegmentCoords,
  bottomCoords,
  isHighlighted,
  isBottomSegmentCurved,
  seriesIndex
}: AreaSeriesProps) {
  const style = {
    fill: color,
    opacity: (isHighlighted ? 1 : 0.3) * areaColorOpacity,
    pointerEvents: areaColorOpacity !== 1 ? 'none' : 'all'
  };

  return (
    <>
      {lineSegmentCoords.map((lineSegmentCoords: Point[]) => {
        let baseCoords;
        if (bottomCoords) {
          // custom bottom coords. eg: top of last series in stacked area
          baseCoords = bottomCoords;
        } else {
          // TODO: make this more generic when we support apps setting baseline
          // for log assume domain min is the baseline, otherwise use 0 baseline
          const baselineCoord = yScale.transform(isLog ? yScale.domain()[0] : 0);
          baseCoords = isHorizontal
            ? ([
                [baselineCoord, lineSegmentCoords[0][1]],
                [baselineCoord, lineSegmentCoords[lineSegmentCoords.length - 1][1]]
              ] as Point[])
            : ([
                [lineSegmentCoords[0][0], baselineCoord],
                [lineSegmentCoords[lineSegmentCoords.length - 1][0], baselineCoord]
              ] as Point[]);
        }
        const cmds = getCmdsForArea(
          lineSegmentCoords,
          baseCoords,
          lineType,
          isHorizontal,
          isBottomSegmentCurved
        );
        return (
          <path d={cmds} style={style} data-oj-series-index={seriesIndex} data-oj-object={'area'} />
        );
      })}
    </>
  );
}
